import { resolveAssetsFromEntry } from '../utils/resolveAssetsFromEntry';
import { parseIgnoreAsset } from '../utils/parseIgnoreAsset';
import { parseCORS } from '../utils/parseCORS';

import type { AppPlugin } from '../Plugin';
import type { AppEntry } from '../Def';
import { Page } from '../Page';

declare module '../Def' {
    interface AppPluginOptions {
        entry?: string | AppEntry;
        preferCORS?: PreferCORS;
        ignoreAsset?: IgnoreAsset;
    }
}

// respect to qiankun
function getPublicPath(entry: string): string {
    try {
        const { origin, pathname } = new URL(entry, location.href);
        const paths = pathname.split('/');
        // remove last
        paths.pop();
        return `${origin}${paths.join('/')}/`;
    } catch (e) {
        console.warn(`Failed to get publicPath from ${entry}:`, e);
        return '';
    }
}

export function createLoadFromEntryPlugin<AdditionalOptions, CustomProps>(): AppPlugin<AdditionalOptions, CustomProps> {
    return ({ app }) => {
        let page: Page<CustomProps> | null = null;
        const { options, lifecycle } = app;

        const parseEntry = (entry: string | AppEntry): AppEntry => {
            if ('string' === typeof entry) {
                return {
                    url: entry,
                };
            }

            return entry;
        };

        if (!('entry' in options)) {
            return;
        }

        lifecycle.hooks.load.tapPromise('LoadFromEntryPlugin', () => {
            return Promise.resolve()
                .then(() => {
                    if (!options.entry) {
                        return Promise.reject(Error(`entry cannot be "${options.entry}"`));
                    }
                    const crossOrigin = parseCORS(options.preferCORS);
                    const entry = parseEntry(options.entry);
                    return resolveAssetsFromEntry(entry, crossOrigin).then(scriptAndStyle => ({
                        scriptAndStyle,
                        entry,
                    }));
                })
                .then(({ scriptAndStyle, entry }) => {
                    const { scripts, styles, isHTML } = scriptAndStyle;
                    const ignoreAsset = parseIgnoreAsset(options.ignoreAsset);

                    page = new Page<CustomProps>({
                        scripts: scripts.filter(s => (s.src ? !ignoreAsset(s.src) : true)),
                        styles: styles.filter(s => (s.href ? !ignoreAsset(s.href) : true)),
                        name: options.name,
                    });

                    let env: Record<string | symbol, unknown> = {};

                    if (isHTML) {
                        env.__INJECTED_PUBLIC_PATH_BY_BLUEBELL__ = getPublicPath(entry.url);
                    }

                    env = {
                        ...env,
                        ...options.envWithinLoading,
                    };

                    return page.resolveLifecycle(!options.loose, options.fetchResourceOptions, env);
                });
        });

        lifecycle.hooks.beforemount.tapPromise('LoadFromEntryPlugin', () => {
            return Promise.resolve(page?.show());
        });

        lifecycle.hooks.afterunmount.tapPromise('LoadFromEntryPlugin', () => {
            return Promise.resolve(page?.hide());
        });

        app.hooks.afterunload.tapPromise('LoadFromEntryPlugin', () => {
            return Promise.resolve(page?.destroy());
        });
    };
}
